import { EyeOutlined } from '@ant-design/icons';
import { Avatar, Button, Empty, Image, List, Pagination, Rate, Skeleton, Spin } from 'antd';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import { Viewport } from 'components/ViewportProvider';
import { push } from 'connected-react-router';
import {
  checkCanReviewProduct,
  productReviews as productReviewsActions,
  submitReviewProduct,
} from 'containers/ProductDetailsPage/actions';
import { selectProduct, selectProductReviews } from 'containers/ProductDetailsPage/selectors';
import { ContainerState, ProductReviewsType } from 'containers/ProductDetailsPage/types';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { css } from 'styles/styled-components';
import translations from 'translations';
import { ApplicationRootState } from 'types/index';
import { FeedbackProductInput, FeedbackRating } from 'types/schema';
import media from 'utils/mediaStyle';
import utilMessages from 'utils/messages';
import AnonymousIcon from '../_icons/anonymousIcon.svg';
import EmptyIcon from '../_icons/emptyIcon.svg';
import SecurityFillIcon from '../_icons/securityFillIcon.svg';
import VideoPlayIcon from '../_icons/VideoPlayIcon.svg';

import AddReviewProductModal from './AddReviewProductModal';

const Container = styled.div`
  margin-top: 16px;

  ${media.lg`
    margin-top: 40px;
  `}

  .ant-list-item {
    padding: 16px 0px !important;
  }
  .ant-empty-image {
    height: auto !important;
    margin-bottom: 16px !important;
  }
  .ant-image {
    display: none !important;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const HeaderTitle = styled.h1`
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  color: #343538;

  ${media.lg`
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;
  `}
`;

const AddReviewButton = styled(Button)`
  padding: 7px 12px !important;
  border-radius: 6px !important;

  color: #2f382c !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  line-height: 18px !important;

  ${media.md`
    padding: 11px 16px !important;
    border-radius: 8px !important;
  `}
`;

const AverageRatingContainer = styled.div`
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  gap: 8px;
  border-radius: 8px;
  background: #f7f5ee;

  ${media.md`
    padding: 12px;
    flex-direction: row;
    align-items: center;
    gap: 12px

  `}

  ${media.lg`
    padding: 16px;
    gap: 16px;
  `}
`;

const AverageRatingValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AverageRatingValue = styled.div`
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  color: #2f382c;

  ${media.md`
    font-size: 30px;
    line-height: 40px;
  `}
`;

const AverageRatingStarsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Stars = styled(Rate)`
  .ant-rate-star-zero {
    color: #eaebea !important;
  }
`;

const FilterStarsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  width: 100%;

  ${media.md`
    flex-wrap: wrap;
    width: auto;
  `}
`;

const FilterStarBtn = styled(Button)`
  padding: 6px 12px !important;
  border-radius: 20px !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 20px !important;
  color: #343538 !important;
  background: #ffffff !important;
  border: 1px solid #d5d7d5 !important;
  box-sizing: border-box !important;

  &:hover,
  &:focus {
    color: #43a047 !important;
    border: 1px solid #43a047 !important;
  }

  ${({ isSelected }) =>
    isSelected &&
    `
    color: #43a047 !important;
    border: 1px solid #43a047 !important;
  `}
`;

const RatingItemContainer = styled.div`
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-bottom: 1px solid #eaebea;
  position: relative;

  ${({ isLast }) =>
    isLast &&
    `
    border-bottom: none;
  `}

  ${media.md`
    flex-direction: row;
    padding: 24px 0;
    gap: 24px;

  `}
`;

const RatingItemUserInfoWrapper = styled.div`
  width: auto;
  display: flex;
  align-items: center;
  gap: 8px;

  ${media.md`
    align-items: flex-start;
    width: 180px !important;
    gap: 12px;

  `}
`;

const RatingItemUserAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;

  ${media.md`
    width: 40px;
    height: 40px;
  `}
`;

const RatingItemUserInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;

  ${media.md`
    flex-direction: column;
    align-items: flex-start;
  `}
`;
const RatingItemUserName = styled.div`
  max-width: 100px;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: #2f382c;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${media.md`
    font-size: 16px;
    line-height: 20px;
  `}
`;

const RatingItemUserNoteContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
`;

const RatingItemUserNote = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: #43a047;

  ${media.md`
    font-size: 14px;
    line-height: 18px;
  `}
`;

const RatingContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RatingContentText = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #596056;
  word-break: break-word;

  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const RatingImagesList = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  ${media.md`
  margin-top: 4px;
  `}
`;

const OverlayImageContainer = styled.div`
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
`;

const RatingImageItemWrapper = styled.div`
  width: 62px;
  height: 62px;
  border-radius: 8px;
  border: 1px solid #eaebea;
  object-fit: cover;
  cursor: pointer;
  position: relative;

  &:hover ${OverlayImageContainer} {
    display: flex;
  }
`;

const RatingImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

const VideoFrame = styled.video`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  object-fit: cover;
`;

const RatingTime = styled.div`
  position: absolute;
  top: 24px;
  right: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #757d73;
`;

const EmptyReviewText = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #2f382c;

  ${media.md`
    font-size: 16px;
    line-height: 24px;
  `}
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const PlayIconContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const alternativeImage = (name) => `https://ui-avatars.com/api/?name=${name}&size=512`;

const AverageStarRating = ({ ratio, size = 20, maskId }) => {
  const safeRatio = Math.max(0, Math.min(1, ratio));

  const clipWidth = safeRatio * 20;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 20 21"
      fill="none"
      style={{ display: 'block' }}
    >
      <defs>
        <mask id={maskId} x="0" y="0" width="20" height="21" maskUnits="userSpaceOnUse">
          <rect width={`${clipWidth}`} height="21" x="0" y="0" fill="#FFFFFF" />
        </mask>
      </defs>

      <path
        d="M9.32715 0.43457C9.5123 0.0488281 9.9627 -0.110352 10.333 0.0800781C10.4805 0.155777 10.6002 0.280868 10.6729 0.43457L13.333 6.05176L19.2832 6.95312C19.6933 7.01659 19.9792 7.41168 19.916 7.83887C19.8926 8.00977 19.8154 8.1665 19.6982 8.28613L15.3926 12.6582L16.4102 18.833C16.4803 19.2577 16.2055 19.6602 15.7979 19.7334C15.6362 19.7626 15.4675 19.7358 15.3223 19.6553L10 16.7402L4.67676 19.6553C4.55314 19.7232 4.41384 19.7544 4.27441 19.7441C4.1349 19.7338 4.00088 19.6823 3.8877 19.5967C3.77448 19.511 3.68643 19.3938 3.63379 19.2588C3.58125 19.1239 3.56607 18.9765 3.58984 18.833L4.60645 12.6582L0.301758 8.28613C0.159292 8.14147 0.07697 7.94354 0.0742188 7.73633C0.0715819 7.52895 0.14827 7.32834 0.287109 7.17969C0.401867 7.05772 0.551909 6.9776 0.715820 6.95312L6.66699 6.05176L9.32715 0.43457Z"
        fill="#EAEAEB"
      />

      <g mask={`url(#${maskId})`}>
        <path
          d="M9.32715 0.43457C9.5123 0.0488281 9.9627 -0.110352 10.333 0.0800781C10.4805 0.155777 10.6002 0.280868 10.6729 0.43457L13.333 6.05176L19.2832 6.95312C19.6933 7.01659 19.9792 7.41168 19.916 7.83887C19.8926 8.00977 19.8154 8.1665 19.6982 8.28613L15.3926 12.6582L16.4102 18.833C16.4803 19.2577 16.2055 19.6602 15.7979 19.7334C15.6362 19.7626 15.4675 19.7358 15.3223 19.6553L10 16.7402L4.67676 19.6553C4.55314 19.7232 4.41384 19.7544 4.27441 19.7441C4.1349 19.7338 4.00088 19.6823 3.8877 19.5967C3.77448 19.511 3.68643 19.3938 3.63379 19.2588C3.58125 19.1239 3.56607 18.9765 3.58984 18.833L4.60645 12.6582L0.301758 8.28613C0.159292 8.14147 0.07697 7.94354 0.0742188 7.73633C0.0715819 7.52895 0.14827 7.32834 0.287109 7.17969C0.401867 7.05772 0.551909 6.9776 0.715820 6.95312L6.66699 6.05176L9.32715 0.43457Z"
          fill="#FFCE24"
        />
      </g>
    </svg>
  );
};
interface IDispatchProps {
  onFilter: (data: { rating?: number; page?: number }) => void;
  onCheckCanReviewProduct: (data: { callback: (canReview: boolean) => void }) => void;
  onSubmitReviewProduct: (data: { callback: (success: boolean) => void; input: FeedbackProductInput }) => void;
  gotoSignIn: () => void;
}

interface IStateProps {
  product?: ContainerState['product'];
  productReviews?: ProductReviewsType;
}
type IProps = IStateProps & IDispatchProps;
const RatingAndComments = (props: IProps) => {
  const { product, productReviews, onFilter, onCheckCanReviewProduct, onSubmitReviewProduct, gotoSignIn } = props;
  const loggedIn = !!localStorage.getItem('token');

  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [previewFiles, setPreviewFiles] = useState<
    {
      type: 'IMAGE' | 'VIDEO';
      url: string;
    }[]
  >([]);
  const [previewIndex, setPreviewIndex] = useState(0);

  const listActiveStarData: {
    items: FeedbackRating[];
    totalCount: number;
    average: string;
  } = useMemo(() => {
    const activeStars: FeedbackRating[] = [];
    if (!productReviews?.countFeedbackRating?.length) {
      return {
        items: activeStars,
        totalCount: 0,
        average: '0',
      };
    }

    productReviews?.countFeedbackRating?.forEach((item) => {
      if (item?.count) {
        activeStars.push(item);
      }
    });
    const totalCount = activeStars?.reduce((total, star) => total + (star?.count || 0), 0);
    let average = 0;
    if (totalCount > 0) {
      average =
        productReviews?.countFeedbackRating.reduce(
          (total, star) => total + (star?.count || 0) * (star?.rating || 0),
          0,
        ) / totalCount;
    }
    const items = activeStars.sort((a, b) => (b?.rating as number) - (a?.rating as number));

    return {
      items: items,
      totalCount: totalCount,
      average: average.toFixed(1),
    };
  }, [productReviews?.countFeedbackRating]);

  const checkOpenReviewModal = useCallback(() => {
    if (!loggedIn) {
      gotoSignIn();
      return;
    }
    onCheckCanReviewProduct({
      callback: async (canReview) => {
        if (canReview) {
          setOpenReviewModal(true);
        }
        return;
      },
    });
  }, [gotoSignIn, loggedIn, onCheckCanReviewProduct]);

  return !product?.loading ? (
    <Viewport.Consumer>
      {({ width }) => {
        const isMobile = width < 768;

        return (
          <Container>
            <HeaderContainer>
              <HeaderTitle>{translations(utilMessages.productReview)}</HeaderTitle>
              {!!productReviews?.data?.length && (
                <AddReviewButton size="large" onClick={checkOpenReviewModal}>
                  {translations(utilMessages.review)}
                </AddReviewButton>
              )}
            </HeaderContainer>
            {!productReviews?.data?.length ? (
              productReviews?.loading ? (
                <List
                  itemLayout="vertical"
                  size="large"
                  dataSource={[0, 1]}
                  renderItem={(item) => (
                    <List.Item key={item}>
                      <Skeleton loading={true} active avatar>
                        <List.Item.Meta avatar={<Avatar />} />
                      </Skeleton>
                    </List.Item>
                  )}
                />
              ) : (
                <Empty
                  image={<SVGIcon src={EmptyIcon} />}
                  style={{
                    marginTop: '40px',
                  }}
                  description={
                    <>
                      <EmptyReviewText>{translations(utilMessages.noReviewsYet)}</EmptyReviewText>
                      <EmptyReviewText>
                        {translations(!loggedIn ? utilMessages.signinToReview : utilMessages.beTheFirstToReview)}
                      </EmptyReviewText>
                    </>
                  }
                >
                  <AddReviewButton size="large" onClick={checkOpenReviewModal}>
                    {translations(!loggedIn ? utilMessages.signin : utilMessages.review)}
                  </AddReviewButton>
                </Empty>
              )
            ) : (
              <>
                <AverageRatingContainer>
                  <AverageRatingValueContainer>
                    <AverageRatingValue>{listActiveStarData?.average}</AverageRatingValue>
                    <AverageRatingStarsContainer>
                      {[0, 1, 2, 3, 4].map((index) => (
                        <AverageStarRating
                          key={index}
                          ratio={
                            Number(listActiveStarData?.average) - index >= 1
                              ? 1
                              : Number(listActiveStarData?.average) - index > 0
                                ? Number(listActiveStarData?.average) - index
                                : 0
                          }
                          maskId={`average-star-mask-${index}`}
                        />
                      ))}
                    </AverageRatingStarsContainer>
                  </AverageRatingValueContainer>
                  {!!listActiveStarData?.items?.length && (
                    <FilterStarsContainer>
                      <FilterStarBtn
                        key={'0'}
                        onClick={() => {
                          onFilter({ rating: undefined, page: 0 });
                        }}
                        isSelected={!productReviews?.rating}
                      >
                        {translations(utilMessages.all)} ({listActiveStarData?.totalCount})
                      </FilterStarBtn>
                      {listActiveStarData?.items?.map((star) => (
                        <FilterStarBtn
                          key={star.rating}
                          onClick={() => {
                            onFilter({ rating: star.rating as number, page: 0 });
                          }}
                          isSelected={productReviews?.rating === star.rating}
                        >
                          {star.rating} {translations(star.rating === 1 ? utilMessages.star : utilMessages.stars)} (
                          {star.count})
                        </FilterStarBtn>
                      ))}
                    </FilterStarsContainer>
                  )}
                </AverageRatingContainer>
                <Spin spinning={productReviews?.loading}>
                  {productReviews?.data?.map((item, index) => (
                    <RatingItemContainer key={item.id} isLast={index === productReviews?.data?.length - 1}>
                      <RatingItemUserInfoWrapper>
                        {!!item?.userName ? (
                          <RatingItemUserAvatar
                            loading="lazy"
                            src={item?.userImageUrl || alternativeImage(item?.userName)}
                            alt="User Avatar"
                          />
                        ) : (
                          <SVGIcon
                            src={AnonymousIcon}
                            width={isMobile ? 24 : 40}
                            height={isMobile ? 24 : 40}
                            styles={css`
                              align-items: flex-start !important;
                            `}
                          />
                        )}
                        <RatingItemUserInfo>
                          <RatingItemUserName>
                            {item?.userName || translations(utilMessages.anonymous)}
                          </RatingItemUserName>
                          <RatingItemUserNoteContainer>
                            <SVGIcon src={SecurityFillIcon} renderAsImg={false} />
                            <RatingItemUserNote>{translations(utilMessages.purchased)}</RatingItemUserNote>
                          </RatingItemUserNoteContainer>
                        </RatingItemUserInfo>
                      </RatingItemUserInfoWrapper>
                      <RatingContentWrapper>
                        <Stars
                          disabled
                          defaultValue={item?.rating || 5}
                          style={{
                            fontSize: isMobile ? '14px' : '16px',
                            color: '#FFCE24',
                          }}
                        />
                        <RatingContentText>{item?.comment}</RatingContentText>
                        {!!item?.medias?.length && (
                          <RatingImagesList>
                            {item?.medias?.map((media, index) => {
                              return (
                                <RatingImageItemWrapper
                                  key={media?.url}
                                  onClick={() => {
                                    setPreviewIndex(index);
                                    setPreviewFiles(() => {
                                      return (
                                        item?.medias?.map((m) => ({
                                          type: m?.type as 'IMAGE' | 'VIDEO',
                                          url: m?.url as string,
                                        })) || []
                                      );
                                    });
                                  }}
                                >
                                  {media?.type === 'VIDEO' ? (
                                    <>
                                      <VideoFrame src={media?.url} />

                                      <PlayIconContainer>
                                        <SVGIcon src={VideoPlayIcon} renderAsImg={false} />
                                      </PlayIconContainer>
                                    </>
                                  ) : (
                                    <RatingImage loading="lazy" src={media?.url} alt="Rating Image" />
                                  )}
                                  <OverlayImageContainer>
                                    {media?.type === 'VIDEO' ? (
                                      <SVGIcon src={VideoPlayIcon} renderAsImg={false} />
                                    ) : (
                                      <EyeOutlined
                                        style={{
                                          fontSize: '18px',
                                          color: '#ffffff',
                                        }}
                                      />
                                    )}
                                  </OverlayImageContainer>
                                </RatingImageItemWrapper>
                              );
                            })}
                          </RatingImagesList>
                        )}
                      </RatingContentWrapper>
                      <RatingTime>{moment(Number(item?.createdDate || 0) * 1000).format('DD/MM/YYYY')}</RatingTime>
                    </RatingItemContainer>
                  ))}
                  {productReviews?.totalPage > 1 && (
                    <PaginationContainer>
                      <Pagination
                        onChange={(page) => {
                          onFilter({ rating: productReviews?.rating, page: page - 1 });
                        }}
                        total={productReviews.totalResults}
                        current={productReviews?.page + 1}
                        pageSize={5}
                        showSizeChanger={false}
                      />
                    </PaginationContainer>
                  )}
                </Spin>
              </>
            )}

            <AddReviewProductModal
              openReviewModal={openReviewModal}
              setOpenReviewModal={setOpenReviewModal}
              product={product}
              onCheckCanReviewProduct={onCheckCanReviewProduct}
              onSubmitReviewProduct={onSubmitReviewProduct}
            />
            <Image.PreviewGroup
              preview={{
                current: previewIndex,
                visible: previewFiles?.length > 0,
                movable: true,
                maskClosable: false,
                destroyOnClose: true,
                imageRender: (_, element) => {
                  const mediaItem = previewFiles.find((f) => f.url === element?.image?.url);
                  if (mediaItem?.type === 'VIDEO') {
                    return (
                      <video
                        style={{ width: '100%', maxWidth: 800, height: '80%', objectFit: 'contain' }}
                        src={element?.image?.url}
                        controls
                        autoPlay
                      />
                    );
                  }
                  return (
                    <img
                      style={{ width: '100%', maxWidth: 800, height: '80%', objectFit: 'contain' }}
                      src={element?.image?.url}
                      alt="preview"
                    />
                  );
                },
                onChange: (current) => {
                  setPreviewIndex(current);
                },
                onVisibleChange: (visible) => {
                  if (!visible) {
                    setPreviewIndex(0);
                    setPreviewFiles([]);
                  }
                },
                toolbarRender: () => {
                  return null;
                },
              }}
            >
              {previewFiles?.map((image, index) => (
                <Image
                  key={index}
                  src={image?.url}
                  alt={'Hình ảnh đánh giá'}
                  style={{ display: 'none' }} // ẩn các ảnh, chỉ phục vụ preview
                />
              ))}
            </Image.PreviewGroup>
          </Container>
        );
      }}
    </Viewport.Consumer>
  ) : null;
};

const mapStateToProps = createStructuredSelector<ApplicationRootState, IStateProps>({
  product: selectProduct(),
  productReviews: selectProductReviews(),
});
const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  onFilter: (data: { rating?: number; page?: number }) =>
    dispatch(productReviewsActions.request({ ...data, page: data.page || 0 })),
  onCheckCanReviewProduct: (data: { callback: (canReview: boolean) => void }) => dispatch(checkCanReviewProduct(data)),
  onSubmitReviewProduct: (data: { callback: (success: boolean) => void; input: FeedbackProductInput }) =>
    dispatch(submitReviewProduct(data)),
  gotoSignIn: () => dispatch(push('/signin')),
});

const withConnect = connect<IStateProps, IDispatchProps>(mapStateToProps, mapDispatchToProps);

export default withConnect(RatingAndComments);
